import {useContext, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {colors} from 'patient-ping-remedy/packages/theme';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import ContextMenu from 'patient-ping-remedy/packages/context-menu';
import IconButton from 'patient-ping-remedy/packages/icon-button';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {DropdownItemResponse, DropdownItemType} from '@/api/dto/dto';
import {useAlertStore} from '@/store/alert_store';
import DropdownItemModal from '@/components/pages/settings/dropdown_items/DropdownItemModal';
import SortableTable, {SORT_ORDER, TableData} from '@common/SortableTable';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';

const tableHeaders = ['Type', 'Value', 'Label', 'Active', ''];

export default function DropdownItems() {
  const { carecoApi } = useContext(CarecoApiContext);
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editDropdownItem, setEditDropdownItem] = useState<DropdownItemResponse | null>(null);

  const { data: dropdownItems, isLoading, error } = useQuery({
    queryKey: ['dropdownItems', true],
    queryFn: () => carecoApi?.getDropdownItems([], true),
    enabled: !!carecoApi,
  });

  const { mutate: enableDropdownItem } = useMutation({
    mutationFn: async (id: number) => carecoApi?.enableDropdownItem(id),
    onSuccess(){
      void queryClient.invalidateQueries({ queryKey: ['dropdownItems'] });
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Dropdown Item enabled successfully.'
      });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to enable Dropdown Item. ${Helpers.traceId(error)}`, error);
    },
  });

  const { mutate: deleteDropdownItem } = useMutation({
    mutationFn: async (id: number) => carecoApi?.deleteDropdownItem(id),
    onSuccess(){
      void queryClient.invalidateQueries({ queryKey: ['dropdownItems'] });
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Dropdown Item removed successfully.'
      });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to remove Dropdown Item. ${Helpers.traceId(error)}`, error);
    },
  });

  const tableData: TableData[] = (dropdownItems ?? []).map((dropdownItem) => {
    const menuItems = [
      {
        action: () => setEditDropdownItem(dropdownItem),
        label: 'Edit',
      },
    ];

    if (dropdownItem.id) {
      menuItems.push(dropdownItem.active ? {
        action: () => deleteDropdownItem(dropdownItem.id!),
        label: 'Remove',
      } : {
        action: () => enableDropdownItem(dropdownItem.id!),
        label: 'Enable',
      });
    }

    return {
      type: {
        tableHeader: 'Type',
        key: 'type',
        type: 'string',
        value: DisplayHelpers.humanize(dropdownItem.type),
        displayValue: DisplayHelpers.humanize(dropdownItem.type),
        columnWidth: '20%',
      },
      value: {
        tableHeader: 'Value',
        key: 'value',
        type: 'string',
        value: dropdownItem.value,
        displayValue: dropdownItem.value,
        columnWidth: '20%',
      },
      label: {
        tableHeader: 'Label',
        key: 'label',
        type: 'string',
        value: dropdownItem.label,
        displayValue: dropdownItem.label,
        columnWidth: '50%',
      },
      active: {
        tableHeader: 'Active',
        key: 'active',
        type: 'string',
        value: dropdownItem.active ? 'Yes' : 'No',
        displayValue: dropdownItem.active ? 'Yes' : 'No',
        columnWidth: '10%',
      },
      actions: {
        tableHeader: '',
        key: 'actions',
        type: 'custom',
        value: '',
        columnWidth: '10%',
        displayValue: (
          <ContextMenu
            menuItems={menuItems}
            position="bottom"
            menuTestId="dropdown-item-context-menu"
            triggerTestId="dropdown-item-context-menu"
          >
            <IconButton iconClass={ICONS['ellipsis-h']} styleType={StyleType.TERTIARY}/>
          </ContextMenu>
        )
      }
    };
  });

  return (
    <>
      <SortableTable
        id="dropdown-items-table"
        data={tableData}
        loading={isLoading}
        loadError={error}
        header={tableHeaders}
        sortableColumns={tableData.length > 0 ? [
          tableData[0]['value'],
          tableData[0]['label']
        ] : []}
        searchColumns={['value', 'label']}
        searchPlaceholder="Search by dropdown item value or label"
        filterOptions={[
          {
            type: 'multiselect-search',
            label: 'Type',
            key: 'type',
            dataType: 'string',
            options: Object.values(DropdownItemType).map((type) => ({
              id: type,
              value: type,
              label: DisplayHelpers.humanize(type),
            })),
          },
          {
            type: 'multiselect-search',
            key: 'active',
            label: 'Active',
            dataType: 'string',
            options: [
              { label: 'Yes', value: 'Yes', id: 'Yes' },
              { label: 'No', value: 'No', id: 'No' },
            ],
          },
        ]}
        tableOverrides={{
          initialFilters: {
            active: [
              { label: 'Yes', value: 'Yes', id: 'Yes' },
            ],
          },
          initialSort: tableData[0] ? {
            tableColumn: tableData[0].value,
            sortOrder: SORT_ORDER.ASC
          } : null
        }}
      >
        <Button onClick={() => setCreateModalOpen(true)}>
          <Icon iconClass={ICONS.plus} color={colors.white} style={{ marginRight: '10px' }}/>
          Add Dropdown Item
        </Button>
      </SortableTable>
      <DropdownItemModal
        isOpen={createModalOpen || !!editDropdownItem}
        dropdownItem={editDropdownItem}
        close={() => {
          setCreateModalOpen(false);
          setEditDropdownItem(null);
        }}
      />
    </>
  );
}
